/* eslint-disable no-shadow, no-param-reassign */

import { Userpilot } from 'userpilot';

import Fonts from '../../services/fonts';
import Partners from '../../services/partners';
import Plans from '../../services/plans';
import Snippets from '../../services/snippets';
import Tagboards from '../../services/tagboards';
import Users from '../../services/users';

const lambdaHeader = process.env.LAMBDA_AUTH_HEADER;
const lambdaHeaderValue = process.env.LAMBDA_AUTH_HEADER_VALUE;

const defaultTagboards = [{
  _id: process.env.DEFAULT_TAGBOARD_ID || 396837,
  name: 'Example Board',
}];

const Permissions = [
  'graphics', // Still necessary? Doesn't everybody have graphics?
  'adobe_conversion',
  'sheet_data_element',
  'sportradar_element',
  'shoppable',
  'video_element',
  'custom_fonts',
  'fox_foresight_element',
];

const state = {
  user: null,
  plan: null,
  accounts: null,
  owner: null, // TODO: Determine via query param, cookie, route param, or something
  isStaff: false, // TODO: Determine from user groups after loading /info
  isDev: false, // TODO: Determine from user groups after loading /info
  tagboards: [...defaultTagboards],
  hashtags: [],
  snippets: [],
  apiKeys: {},
  customFonts: [],

  // PDK
  partners: [],

  // QR Code Analytics
  qrCodeStats: {},
  showScanLimitWarning: false,

  permissionWarning: null,
};

const actions = {
  async getUserAccounts(context) {
    const data = await Plans.getUserAccounts(context.state.user.id);
    context.commit('SET_USER_ACCOUNTS', data);
  },

  async getUserSnippets(context) {
    const { owner } = context.state;
    const data = await Snippets.getSnippets(owner);
    context.commit('SET_USER_SNIPPETS', data?.snippets || []);
  },

  async getUserTagboards(context) {
    const data = await Tagboards.getTagboards(context.state.owner);
    context.commit('SET_USER_TAGBOARDS', data.results);
    return data;
  },

  async getPartners(context) {
    const data = await Partners.getPartners().catch((err) => {
      console.error('Failed to fetch partners:', err); // eslint-disable-line no-console
      return {};
    });

    context.commit('SET_PARTNERS', data.partners);
    return data.partners ?? [];
  },

  setOwner(context, owner) {
    context.commit('SET_OWNER', owner);
  },

  /**
   * Get information about logged-in user and store in state.
   */
  async getInfo(context) {
    const user = await Users.getInfo();
    context.commit('SET_USER', user);

    // Only identify user in Userpilot if NOT staff and we are in production.
    if (!user.groups?.includes('staff') && process.env.NODE_ENV === 'production') {
      Userpilot.identify(user.id, {
        name: user.profile.displayName,
        email: user.email,
      });
    }

    // Set user in Sentry so we can easily see who caused an exception.
    window.Sentry?.setUser({
      id: user.id,
      email: user.email,
    });
  },

  /**
   * Fetch plan permissions for current account owner needed to toggle
   * availability to certain graphic elements.
   */
  async getPermissions(context) {
    const { owner } = context.state;
    const permissions = await Plans.checkPermissions(owner, Permissions);
    context.commit('SET_PLAN', { permissions });
  },

  /**
   * Fetch list of third-party API keys for user, and store in state.
   */
  async getUserApiKeys({ state, commit }) {
    const apiKeyData = await Users.getApiKeys(state.owner);
    commit('SET_USER_API_KEYS', apiKeyData);
    return apiKeyData;
  },

  async getFonts(context) {
    const fonts = await Fonts.getFonts(context.rootState.user.owner);
    context.commit('GET_CUSTOM_FONTS', fonts || []);
  },

  /**
   * Fetch total number of QR code scans for the account owner.
   * Used to show warnings when adding new QR code elements
   * TODO: Move network request, along with header setup to a separate service file.
   */
  async getQRCodeScans(context) {
    const { owner } = context.rootState.user;

    const resp = await fetch(`${process.env.TAGBOARD_SHORT_URLS_URL}/stats/${owner}`, {
      credentials: 'include',
      mode: 'cors',
      headers: {
        [lambdaHeader]: lambdaHeaderValue,
      },
    });

    const body = await resp.json();
    context.commit('SET_QR_CODE_STATS', body?.data || {});
  },

  setShowScanLimitWarning(context, isVisible) {
    context.commit('SET_SHOW_SCAN_LIMIT_WARNING', isVisible);
  },

  setPermissionWarning(context, permissionWarning) {
    context.commit('SET_PERMISSION_WARNING', permissionWarning);
  },
};

const mutations = {
  SET_USER_ACCOUNTS(state, payload) {
    state.accounts = payload;

    // If owner is set, but not included in list of valid accounts,
    // set it to something valid instead.
    const ownerIds = Object.keys(state.accounts.owners).map(Number);
    if (!ownerIds.includes(state.owner)) {
      state.owner = state.accounts.admin; // Reset back to the logged-in user.
    }
  },

  SET_OWNER(state, payload) {
    state.owner = payload;
  },

  SET_USER(state, user) {
    state.user = user;
    state.isStaff = user?.groups?.includes('staff');
    state.isDev = user?.groups?.includes('dev');

    if (!state.owner) {
      // If owner wasn't set via query params, then assume logged-in user is owner.
      state.owner = user.id;
    }
  },

  SET_PLAN(state, plan) {
    state.plan = plan;
  },

  SET_USER_SNIPPETS(state, snippets = []) {
    state.snippets = snippets;
  },

  SET_USER_TAGBOARDS(state, payload) {
    state.tagboards = [...defaultTagboards, ...payload];
  },

  SET_PARTNERS(state, payload) {
    state.partners = payload ?? [];
  },

  SET_USER_API_KEYS(state, payload) {
    state.apiKeys = payload;
  },

  GET_CUSTOM_FONTS(state, payload) {
    state.customFonts = payload;
  },

  SET_QR_CODE_STATS(state, payload) {
    state.qrCodeStats = payload;
  },

  SET_SHOW_SCAN_LIMIT_WARNING(state, showScanLimitWarning) {
    state.showScanLimitWarning = showScanLimitWarning;
  },

  SET_PERMISSION_WARNING(state, permissionWarning) {
    state.permissionWarning = permissionWarning;
  },
};

const getters = {
  owner(state) {
    return +state.owner;
  },

  isStaff(state) {
    return state.user.isStaff;
  },

  hasSheetDataElement(state) {
    return state.plan.permissions.sheet_data_element;
  },

  hasSportradarElement(state) {
    return state.plan.permissions.sportradar_element?.length > 0;
  },

  hasVideoElement(state) {
    return state.plan.permissions.video_element;
  },

  hasShoppable(state) {
    return state.plan.permissions.shoppable;
  },

  hasFoxForesight(state) {
    return state.plan.permissions.fox_foresight_element;
  },

  /**
   * The total number of QR Code scans for the account (for the current license period)
   */
  totalScans(state) {
    return state.qrCodeStats.totalScans || 0;
  },

  /**
   * Whether or not the owner is currently over their scan limit.
   * If scan limit is set to 0, then ignore the limit.
   * @returns {boolean} - True if user has exceeded scan limit
   */
  overScanLimit(state, getters) {
    const { qr_code_scans: scanLimit } = state.plan.permissions;
    return scanLimit > 0 && getters.totalScans > scanLimit;
  },

  customFonts(state) {
    return state.customFonts;
  },

  customFontsArray(state) {
    return state.customFonts.reduce((memo, item) => {
      const [name] = item.title.split('.');

      return memo.concat([{
        cssClass: name,
        name,
        font: name,
        id: item._id,
        fontId: `f-${item._id}`,
      }]);
    }, []);
  },

  availableSportradarLeagues(state) {
    return state.plan.permissions.sportradar_element || [];
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
